import {
	BulkInvoiceCreateModal_company,
	BulkInvoiceCreateModal_project,
	BulkInvoiceCreateModal_retainerPeriod,
	InvoiceDTO,
} from './BulkInvoiceCreateModal';
import {addQuantityDecimals} from '../../../../../../../components/insights/invoicing/line-generation/invoicing_generate_lines_utils';
import {getUnitPrice} from '../../../../../../project-tab/projects/project-retainer-periods/RetainerPeriodUtil';
import {dateFragmentPropsFromIsoDateString} from '../../../../../util/DateUtil';
import Util from '../../../../../../shared/util/util';
import CreateInvoiceMutation from '../../../../../../../mutations/create_invoice_mutation';
import {
	invoiceLineConverter,
	workDetailRowConverter,
} from '../../../../../../../containers/modal/create_invoice_modal_v2/invoice_converters';
import {notNullishAndPredicate} from '../../../../../util/NotNullPredicate';
import {addDays, format, formatISO} from 'date-fns';
import {getTimeRegsForPeriod} from '../../../../../../project-tab/projects/project-retainer-periods/RetainerPeriodUtil';
import {buildWorkDetailRows} from '../../../../../../../components/insights/invoicing/line-generation/invoicing_work_detail_rows';
import {workDetailPriceConverter} from '../../../../../../../containers/modal/create_invoice_modal_v2/work_detail_currency';

export interface InvoicePeriodDTO {
	name: string | null | undefined;
	quantity: number;
	unitPrice: number;
	invoiceDate: string | null;
	dueDate: string | null;
	period: BulkInvoiceCreateModal_retainerPeriod;
	errorMessage?: string;
}

export interface CurrencyItem {
	id: string;
	currencyCode: string;
}

function getDueDate(period: BulkInvoiceCreateModal_retainerPeriod): string | null {
	if (!period?.endYear || !period.endMonth || !period.endDay) {
		return null;
	}
	return formatISO(addDays(new Date(period.endYear, period.endMonth - 1, period.endDay), 30), {representation: 'date'});
}

function getInvoiceDate(period: BulkInvoiceCreateModal_retainerPeriod): string | null {
	if (!period?.endYear || !period.endMonth || !period.endDay) {
		return null;
	}
	return formatISO(new Date(period.endYear, period.endMonth - 1, period.endDay), {representation: 'date'});
}

function getPeriodQuantity(period?: BulkInvoiceCreateModal_retainerPeriod): number {
	if (period?.periodBudgetType === 'FIXED_HOURS') {
		return period?.periodHoursAmount || 0;
	} else if (period?.periodBudgetType === 'FIXED_PRICE') {
		return 1;
	}
	return 0;
}

export function getCurrencyItems(company: BulkInvoiceCreateModal_company): CurrencyItem[] {
	if (!company || !company.dateEffectiveExchangeRates?.edges) {
		return [];
	}
	const currencyItems: CurrencyItem[] = [];
	if (company.currency) {
		currencyItems.push({id: company.currency, currencyCode: company.currency});
	}
	company.dateEffectiveExchangeRates.edges.forEach(edge => {
		const i = edge?.node;
		if (i && !!i.currency) {
			currencyItems.push({id: i.currency, currencyCode: i.currency});
		}
	});
	return currencyItems;
}

type getInvoicePeriodDataParams = {
	periods?: (BulkInvoiceCreateModal_retainerPeriod | undefined)[];
	currency: string;
	project: BulkInvoiceCreateModal_project;
	company: BulkInvoiceCreateModal_company;
};

export function getInvoicePeriodData({periods, currency, project, company}: getInvoicePeriodDataParams): InvoicePeriodDTO[] {
	if (!company || !project || !periods) {
		return [];
	}
	return (
		periods.filter(notNullishAndPredicate()).map(period => {
			return {
				period: period,
				name: period.name,
				quantity: getPeriodQuantity(period),
				unitPrice: getUnitPrice({
					period: period,
					invoiceCurrency: currency,
					rateCard: project.rateCard,
					company: company,
					timeRegs: project.timeRegistrations?.edges,
				}),
				invoiceDate: getInvoiceDate(period),
				dueDate: getDueDate(period),
			};
		}) || []
	);
}

function getInvoiceEntryDescription(period: BulkInvoiceCreateModal_retainerPeriod) {
	if (!period) {
		return '';
	}
	return `${period.name} (${format(Util.CreateDate(period.startYear, period.startMonth, period.startDay), 'PP')} - ${format(
		Util.CreateDate(period.endYear, period.endMonth, period.endDay),
		'PP'
	)})`;
}

type createInvoicesParams = {
	invoices?: InvoiceDTO[];
	project: BulkInvoiceCreateModal_project;
	/** Needed for its exchange-rate history, which is what prices work detail in the invoice currency. */
	company?: BulkInvoiceCreateModal_company;
	invoiceQuantityDecimals?: number | null;
	onSuccess: () => void;
	includeWorkDetail?: boolean;
	workDetailGrouping?: string;
	showRates?: boolean;
};

/** ISO yyyy-MM-dd for a period's start or end, whose parts are stored separately. */
function isoFromParts(year?: number | null, month?: number | null, day?: number | null): string | null {
	if (!year || !month || !day) return null;
	return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export async function createInvoices({
	invoices,
	project,
	company,
	invoiceQuantityDecimals,
	onSuccess,
	includeWorkDetail = false,
	workDetailGrouping,
	showRates = false,
}: createInvoicesParams): Promise<void> {
	if (!invoices) {
		return;
	}

	const commitInvoiceMutation = invoice => {
		return new Promise<void>(resolve => {
			Util.CommitMutation(CreateInvoiceMutation, invoice, resolve);
		});
	};

	for (let index = 0; index < invoices.length; index++) {
		const invoice = invoices[index];
		const invoiceDate = dateFragmentPropsFromIsoDateString(invoice.invoiceDate);
		const dueDate = dateFragmentPropsFromIsoDateString(invoice.dueDate);
		if (invoice && invoice.period) {
			const invoiceEntryDescription = getInvoiceEntryDescription(invoice.period);
			const workDetailStartDate = isoFromParts(
				invoice.period.startYear,
				invoice.period.startMonth,
				invoice.period.startDay
			);
			const workDetailEndDate = isoFromParts(invoice.period.endYear, invoice.period.endMonth, invoice.period.endDay);
			const newInvoiceData = {
				clientId: project?.client?.id,
				projectId: project?.id,
				invoiceType: null,
				name: invoice.name,
				currency: invoice.currency,
				invoiceReference: null,
				status: 'DRAFT',
				dueDay: dueDate.day,
				dueMonth: dueDate.month,
				dueYear: dueDate.year,
				createdDay: invoiceDate.day,
				createdMonth: invoiceDate.month,
				createdYear: invoiceDate.year,
				notes: '',
				entries: [
					invoiceLineConverter({
						description: invoiceEntryDescription,
						quantity: addQuantityDecimals(invoice.quantity, {invoiceQuantityDecimals}),
						unitPrice: invoice.unitPrice,
						projectId: project?.id,
						tax: invoice.tax,
						discount: 0,
						periodIds: [invoice.period.id],
						timeRegIds: getTimeRegsForPeriod(
							(project?.timeRegistrations?.edges ?? []).filter(
								(edge): edge is NonNullable<typeof edge> => !edge?.node?.invoiced
							),
							invoice.period
						).map(edge => edge.node.id),
					}),
				],
				// Work detail is PURELY ADDITIVE here. A retainer period entry already marks its time
				// invoiced through the timeRegIds above, which is existing and legitimate behaviour for
				// retainers, so this must neither add to nor remove from it. The rows below carry no
				// time-registration ids of their own; they travel in a separate array and only describe
				// what was done.
				includeWorkDetail,
				workDetailGrouping: includeWorkDetail ? workDetailGrouping ?? null : null,
				workDetailStartDate: includeWorkDetail ? workDetailStartDate : null,
				workDetailEndDate: includeWorkDetail ? workDetailEndDate : null,
				workDetailRows: includeWorkDetail
					? buildWorkDetailRows(
							(project?.timeRegistrations?.edges ?? [])
								.filter((edge): edge is NonNullable<typeof edge> => !!edge?.node)
								.map(edge => edge.node as never),
							{
								grouping: workDetailGrouping ?? 'PERSON',
								showRates,
								quantityDecimals: invoiceQuantityDecimals ?? 2,
								// The period's own span is the window: a period invoice is about the work
								// in that period, so there is nothing for the operator to choose.
								startDate: workDetailStartDate ?? undefined,
								endDate: workDetailEndDate ?? undefined,
								// One project, so one source currency for every registration. The
								// invoice carries no date object here, which resolves the exchange
								// rate the same way the period's own unit price does — the two
								// describe the same money and must not disagree about the rate.
								priceToInvoiceCurrency: workDetailPriceConverter(
									company,
									{currency: invoice.currency},
									project?.rateCard?.currency
								),
							}
					  ).map(workDetailRowConverter)
					: [],
			};
			await commitInvoiceMutation(newInvoiceData);
		}
	}
	onSuccess();
}
